import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import MaterialCard from "../../components/MaterialsManagement/MaterialCard";
import MarketplaceFilterBar from "../../components/MaterialsManagement/MarketplaceFilterBar";
import FilterPanel from "../../components/MaterialsManagement/FilterPanel";
import ListingDetailPanel from "../../components/MaterialsManagement/ListingDetailPanel";
import CompaniesMapIframe from "../../components/MaterialsManagement/CompaniesMapIframe";
import MapListingsTable from "../../components/MaterialsManagement/MapListingsTable";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import Skeleton from "../../components/ui/Skeleton";

import { useDarkMode, useDevMode } from "../../contexts/ThemeContext";
import { useMaterialManagementController } from "../../hooks/useMaterialManagementController";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { apiPostJson } from "../../api/client";

import "../../style/ListingCard.css";
import "../../style/MaterialCard.css";
import "../../style/MaterialManagement.css";

const MIN_SPINNER_MS = 600;

function CardSkeleton({ darkMode }) {
  const cls = `lc mc ${darkMode ? "lc--dark" : "lc--light"}`;
  return (
    <div className={cls} style={{ pointerEvents: "none" }}>
      {/* image area */}
      <div className="lc__img" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="lc__img-placeholder" />
      </div>

      {/* dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: 5, padding: "6px 0 2px" }}>
        <Skeleton width="6px" height="6px" variant="circular" />
        <Skeleton width="6px" height="6px" variant="circular" />
        <Skeleton width="6px" height="6px" variant="circular" />
      </div>

      {/* body */}
      <div style={{ padding: "10px 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* title + price row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
            <Skeleton width="70%" height="14px" />
            <Skeleton width="40%" height="10px" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, marginLeft: 12 }}>
            <Skeleton width="56px" height="16px" />
            <Skeleton width="30px" height="8px" />
          </div>
        </div>

        {/* badges row */}
        <div style={{ display: "flex", gap: 6 }}>
          <Skeleton width="64px" height="20px" variant="rectangular" borderRadius="6px" />
          <Skeleton width="48px" height="20px" variant="rectangular" borderRadius="6px" />
          <Skeleton width="54px" height="20px" variant="rectangular" borderRadius="6px" />
        </div>

        {/* date row */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Skeleton width="12px" height="12px" variant="circular" />
          <Skeleton width="52px" height="12px" />
        </div>
      </div>
    </div>
  );
}

export default function MaterialManagement() {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();
  const { registerDevAction, clearDevActions } = useDevMode();
  const { hasPermission, isChecking } = usePermissionGuard("receiver", "browse_materials");
  const c = useMaterialManagementController();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelClosing, setPanelClosing] = useState(false);
  const [debugBorders, setDebugBorders] = useState(false);
  const [showRadius, setShowRadius] = useState(true);

  // page entrance animation (matches donor pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!initialLoading && !c.metaLoading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, c.metaLoading]);
  const togglePanel = useCallback(() => {
    if (isPanelOpen) {
      // animate out, then unmount
      setPanelClosing(true);
      setTimeout(() => { setIsPanelOpen(false); setPanelClosing(false); }, 350);
    } else {
      setIsPanelOpen(true);
    }
  }, [isPanelOpen]);
  const closePanel = useCallback(() => {
    setPanelClosing(true);
    setTimeout(() => { setIsPanelOpen(false); setPanelClosing(false); }, 350);
  }, []);
  const toggleRadius = useCallback(() => setShowRadius((v) => !v), []);

  // map → grid: skeletons fade in, then cards cascade
  const [gridReady, setGridReady] = useState(c.viewMode === "grid");
  const prevViewRef = useRef(c.viewMode);
  useEffect(() => {
    const prev = prevViewRef.current;
    prevViewRef.current = c.viewMode;
    if (c.viewMode === "grid" && prev === "map") {
      setGridReady(false);
      // hold skeletons, then swap to real cards
      const t = setTimeout(() => setGridReady(true), 1400);
      return () => clearTimeout(t);
    }
    if (c.viewMode === "grid") setGridReady(true);
  }, [c.viewMode]);

  // register debug toggle in the demo bar
  useEffect(() => {
    registerDevAction({
      label: debugBorders ? "Debug Borders: ON" : "Toggle Debug Borders",
      onClick: () => setDebugBorders((v) => !v),
    });
    return () => clearDevActions();
  }, [debugBorders, registerDevAction, clearDevActions]);

  const [isProcessing, setIsProcessing] = useState(false);
  const timerRef = useRef(null);
  const showProcessing = useCallback(() => {
    setIsProcessing(true);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
  const hideProcessing = useCallback(() => {
    timerRef.current = setTimeout(() => setIsProcessing(false), MIN_SPINNER_MS);
  }, []);

  const handleApplyFilters = useCallback((f) => {
    showProcessing(); c.applyDrawerFilters(f); hideProcessing();
    setPanelClosing(true);
    setTimeout(() => { setIsPanelOpen(false); setPanelClosing(false); }, 350);
  }, [c, showProcessing, hideProcessing]);

  const handleResetFilters = useCallback(() => {
    showProcessing(); c.resetFilters(); hideProcessing();
  }, [c, showProcessing, hideProcessing]);

  const handleSortChange = useCallback((s) => {
    showProcessing(); c.changeSort(s); hideProcessing();
  }, [c, showProcessing, hideProcessing]);

  const handleCardClick = useCallback((listing) => c.openDetail(listing), [c]);
  const closeDetailPanel = useCallback(() => c.closeDetail(), [c]);

  const submitFillRequest = useCallback(async (payload) => {
    const lid = payload?.listingId ?? payload?.listing_id;
    if (lid != null) c.markPendingOptimistic(lid);
    try { await apiPostJson("/material_management/requests", payload); }
    catch (e) { if (lid != null) c.unmarkPending(lid); throw e; }
  }, [c]);

  const detailCompanyUser = useMemo(() => {
    const cid = c.detailListing?.company_id ?? c.detailListing?.companyId;
    return cid ? c.usersById.get(cid) : null;
  }, [c.detailListing, c.usersById]);

  if (isChecking) return <LoadingSpinner fullPage size={50} message={t("Checking permissions...")} />;
  if (!hasPermission) return null;
  if (initialLoading || c.metaLoading) return <LoadingSpinner fullPage size={50} message={t("Loading the marketplace...")} />;

  const rootClass = `material-management-root ${darkMode ? "dark" : "light"} ${debugBorders ? "mc-debug" : ""} ${isVisible ? "visible" : ""}`;
  const isLoading = c.resultsLoading || isProcessing;
  const isDetailOpen = !!c.detailListing;

  const resultsLabel = c.hasSearched && !isLoading
    ? `${c.total} ${t(c.total === 1 ? "material available" : "materials available")}`
    : null;

  return (
    <div className={rootClass}>
      <div className="mm-page">
        {c.errorMessage && (
          <div className="mm-alert-wrap"><Alert message={c.errorMessage} severity="error" /></div>
        )}

        {/* outer flex: centering body + detail panel */}
        <div className={`mm-zones ${c.viewMode === "map" ? "mm-zones--map" : ""}`}>
          <div className="mm-zones__body">
            {/* bar row */}
            <div className="mm-zones__bar-row">
              <div className="mm-zones__bar-center">
                <MarketplaceFilterBar
                  sortBy={c.sortBy} onSortChange={handleSortChange}
                  onTogglePanel={togglePanel} isPanelOpen={isPanelOpen}
                  activeFilterCount={c.activeFilterCount}
                  viewMode={c.viewMode} onViewModeChange={c.setViewMode} darkMode={darkMode}
                  resultsCount={c.total} resultsLabel={resultsLabel}
                  lastFetched={c.lastFetched} onRefresh={c.refresh} isRefreshing={isLoading}
                  radiusKm={c.filters.radiusKm} showRadius={showRadius} onToggleRadius={toggleRadius}
                  isDetailOpen={isDetailOpen}
                  filters={c.filters}
                  onRemoveFilter={c.removeFilter}
                />
              </div>
              <div className={`mm-zones__bar-spacer mm-zones__bar-spacer--right ${!isDetailOpen ? "mm-zones__bar-spacer--hidden" : ""}`} />
            </div>

            {/* panels row */}
            <div className="mm-zones__panels">
              {/* filter panel — always above map + content */}
              {(isPanelOpen || panelClosing) && (
                <div className={`mm-fp-wrap ${panelClosing ? "mm-fp-wrap--closing" : ""}`}>
                  <FilterPanel
                    open onClose={closePanel}
                    initialFilters={c.filters} materialTypes={c.materialTypesForFilters}
                    categories={c.backendMaterialCategories} users={c.users}
                    onApply={handleApplyFilters} onReset={handleResetFilters} darkMode={darkMode}
                  />
                </div>
              )}

              {/* map — full width row in map mode */}
              {c.viewMode === "map" && (
                <div className="mm-map-full">
                  <CompaniesMapIframe
                    markers={c.markers}
                    center={c.mapCenter}
                    zoom={c.mapZoom}
                    onMarkerClick={c.handleMarkerClick}
                    selectedCompanyId={c.selectedCompany?.id ?? c.selectedCompany?.user_id ?? null}
                    receiverCoords={c.effectiveCoords}
                    radiusKm={showRadius ? c.filters.radiusKm : 0}
                    darkMode={darkMode}
                    title=""
                    height={500}
                  />
                </div>
              )}

              <div className="mm-zone mm-zone--center">
                {isLoading && c.hasSearched && c.viewMode === "grid" && (
                  <div className="mm-processing">
                    <LoadingSpinner size={28} message={t("Searching...")} />
                  </div>
                )}

                {c.viewMode === "grid" && !isLoading && (
                  <>
                    {!c.hasSearched || !gridReady ? (
                      <div className="listing-grid mm-skeleton-enter">
                        {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} darkMode={darkMode} />)}
                      </div>
                    ) : c.listings.length > 0 ? (
                      <div className="listing-grid">
                        {c.listings.map((l) => (
                          <MaterialCard key={l.id} listing={l} darkMode={darkMode}
                            onRequest={handleCardClick}
                            onBookmark={c.toggleSave} isBookmarked={c.savedIds.has(String(l.id))}
                            isSelected={c.detailListing?.id === l.id}
                            receiverLat={c.effectiveCoords?.lat} receiverLon={c.effectiveCoords?.lon} />
                        ))}
                      </div>
                    ) : (
                      <div className={`listing-grid__empty ${darkMode ? "dark" : "light"}`}>
                        <p>{t("No materials found")}</p>
                      </div>
                    )}
                  </>
                )}

                {/* map listings table */}
                {c.viewMode === "map" && (
                  <MapListingsTable
                    listings={c.selectedCompany ? c.companyListings : c.listings}
                    selectedCompany={c.selectedCompany}
                    onClearSelection={c.clearSelectedCompany}
                    onSelectListing={c.openDetail}
                    darkMode={darkMode}
                  />
                )}

                {c.viewMode === "grid" && c.totalPages > 1 && !isLoading && gridReady && (
                  <div className="mm-pagination">
                    <span className="mm-pagination__count">
                      {t("Showing")} {c.listings.length} {t("of")} {c.total}
                    </span>
                    <div className="mm-pagination__controls">
                      <button className="mm-pagination__btn" disabled={c.page <= 1} onClick={() => c.goToPage(c.page - 1)}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <span className="mm-pagination__info">{c.page} / {c.totalPages}</span>
                      <button className="mm-pagination__btn" disabled={c.page >= c.totalPages} onClick={() => c.goToPage(c.page + 1)}>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* detail panel — same row as table, below map */}
              <div className={`mm-zone mm-zone--right ${!isDetailOpen ? "mm-zone--empty" : ""}`}>
                {isDetailOpen && (
                  <ListingDetailPanel
                    listing={c.detailListing} companyUser={detailCompanyUser}
                    onClose={closeDetailPanel} onSubmit={submitFillRequest} darkMode={darkMode}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
